import { useMemo, useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  pointerWithin,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import { api } from "@/api/client";
import { compareCzech, sortCzech } from "@ckgo2/shared";
import { formatDate } from "@/lib/format";
import {
  GUIDE_ASSIGNMENT_ROLES,
  GUIDE_ASSIGNMENT_ROLE_LABELS,
  GUIDE_AVAILABILITY_REASON_LABELS,
  type GuideAssignmentRole,
  type GuideAssignmentStatus,
  type GuideAvailabilityReason,
  type ScheduleConflictSeverity,
} from "@ckgo2/shared/types";
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
  Flag,
  UserPlus,
  Loader2,
  Check,
} from "lucide-react";

// ── Timeline geometry ──
// Two spans: "quarter" (3 months, month-aligned — the planning default) and "6w" (6-week detail).
// The day column shrinks for the quarter so the whole 3 months fits without endless horizontal scroll.
const DAY_W_6W = 26; // px per day column, 6-week detail view
const DAY_W_QUARTER = 13; // px per day column, quarter (3-month) view
const ROW_H = 52;
const LABEL_W = 168; // guide-name column width
const WINDOW_DAYS = 42; // 6-week span
const SHIFT_DAYS = 28; // 6-week prev/next step
const QUARTER_MONTHS = 3; // quarter span
const DAY_MS = 86_400_000;

// Drop a block ONTO another assignment block → swap the two guides; drop it on empty row space →
// reassign. The block droppable nests inside the row droppable, so plain rect-intersection would
// always pick the (bigger) row. pointerWithin + a block-first tiebreak (excluding the block being
// dragged) makes "drop on block = swap" deterministic.
const swapAwareCollision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const activeAsg = String(args.active.id).startsWith("asg:")
    ? String(args.active.id).slice("asg:".length)
    : null;
  const block = hits.find((h) => {
    const sid = String(h.id);
    return sid.startsWith("asg-drop:") && sid.slice("asg-drop:".length) !== activeAsg;
  });
  return block ? [block] : hits;
};

// ── Types mirroring GET /api/admin/schedule ──
interface SGuide {
  id: string;
  name: string;
  livingIn?: string | null;
  homeLocationId?: string | null;
  homeDestinationId?: string | null;
  homeDestinationName?: string | null;
}
interface SAssignment {
  id: string;
  guideId: string;
  tripDateId: string;
  role: GuideAssignmentRole;
  status: GuideAssignmentStatus;
  notes?: string | null;
  startDate: string;
  endDate: string;
  code?: string | null;
  tripId?: string | null;
  tripTitle?: string | null;
  destinationId?: string | null;
  destinationName?: string | null;
}
interface SAvailability {
  id: string;
  guideId: string;
  fromDate: string;
  toDate: string;
  reason: GuideAvailabilityReason;
  notes?: string | null;
}
interface SUnassigned {
  tripDateId: string;
  code?: string | null;
  startDate: string;
  endDate: string;
  tripTitle?: string | null;
  destinationId?: string | null;
  destinationName?: string | null;
}
interface SConflict {
  kind: string;
  severity: ScheduleConflictSeverity;
  assignmentId: string;
  otherAssignmentId?: string;
  message: string;
}
interface ReplacementCandidate {
  guideId: string;
  guideName: string;
  homeLocationId: string | null;
  feasible: boolean; // no hard (red) conflict introduced
  warnings: string[]; // soft "verify connection" reasons (or the red reasons if infeasible)
  score: number;
}
interface ScheduleData {
  window: { from: string; to: string };
  guides: SGuide[];
  guideDestinations: Record<string, { id: string; name: string }[]>;
  assignments: SAssignment[];
  availability: SAvailability[];
  unassigned: SUnassigned[];
  locations: { id: string; name: string }[];
  conflicts: SConflict[];
  conflictsByAssignment: Record<
    string,
    { severity: ScheduleConflictSeverity; conflicts: SConflict[] }
  >;
}

// Deterministic destination → colour (cycled palette).
const DEST_PALETTE = [
  "bg-violet-500",
  "bg-sky-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-teal-500",
  "bg-indigo-500",
  "bg-orange-500",
  "bg-cyan-500",
  "bg-pink-500",
];
const ROLE_INITIAL: Record<GuideAssignmentRole, string> = { lead: "L", co: "C", backup: "B" };

function dayIndex(fromISO: string, dateISO: string): number {
  return Math.round(
    (Date.parse(`${dateISO}T00:00:00Z`) - Date.parse(`${fromISO}T00:00:00Z`)) / DAY_MS,
  );
}
function addDays(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}
function addMonths(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}
function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}
/** Inclusive end of a `months`-long window starting at month-aligned `from` (last day of the Nth month). */
function quarterEnd(from: string, months: number): string {
  return addDays(addMonths(from, months), -1);
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
function monthYear(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(uiLocale(), {
    month: "short",
    year: "numeric",
  });
}

export default function SchedulePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  // Span = the planning horizon. Quarter (3 months, month-aligned) is the default — guides are planned
  // a quarter at a time; 6w zooms into near-term detail. The window snaps to the span's natural anchor.
  const span = params.get("span") === "6w" ? "6w" : "quarter";
  const defaultFrom = span === "quarter" ? monthStart(today) : today;
  const from = params.get("from") || defaultFrom;
  const to =
    params.get("to") ||
    (span === "quarter" ? quarterEnd(from, QUARTER_MONTHS) : addDays(from, WINDOW_DAYS));
  const dayW = span === "quarter" ? DAY_W_QUARTER : DAY_W_6W;
  const fDest = params.get("dest") || "";
  const fGuide = params.get("guide") || "";
  const onlyConflicts = params.get("conflicts") === "1";

  const { data, isLoading } = useQuery({
    queryKey: ["schedule", from, to],
    queryFn: () => api.get<{ data: ScheduleData }>(`/api/admin/schedule?from=${from}&to=${to}`),
  });
  const sd = data?.data;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["schedule"] });

  const reassign = useMutation({
    mutationFn: ({ id, guideId }: { id: string; guideId: string }) =>
      api.patch(`/api/admin/schedule/assignments/${id}`, { guideId }),
    onSuccess: invalidate,
  });
  const createAssignment = useMutation({
    mutationFn: (payload: { guideId: string; tripDateId: string }) =>
      api.post(`/api/admin/schedule/assignments`, {
        ...payload,
        role: "lead",
        status: "tentative",
      }),
    onSuccess: invalidate,
  });
  const updateAssignment = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/api/admin/schedule/assignments/${id}`, patch),
    onSuccess: invalidate,
  });
  const removeAssignment = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/schedule/assignments/${id}`),
    onSuccess: invalidate,
  });
  const swap = useMutation({
    mutationFn: (payload: { assignmentAId: string; assignmentBId: string }) =>
      api.post(`/api/admin/schedule/assignments/swap`, payload),
    onSuccess: invalidate,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [dragLabel, setDragLabel] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ a: SAssignment; x: number; y: number } | null>(null);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  // Step the window: a month at a time in quarter view (keeps month alignment), 4 weeks in 6w view.
  const shiftWindow = (dir: number) => {
    const next = new URLSearchParams(params);
    if (span === "quarter") {
      const nf = addMonths(from, dir);
      next.set("from", nf);
      next.set("to", quarterEnd(nf, QUARTER_MONTHS));
    } else {
      next.set("from", addDays(from, dir * SHIFT_DAYS));
      next.set("to", addDays(to, dir * SHIFT_DAYS));
    }
    setParams(next, { replace: true });
  };
  // Switch the planning horizon and snap back to its default anchor (drop any manual from/to).
  const setSpan = (s: "quarter" | "6w") => {
    const next = new URLSearchParams(params);
    next.set("span", s);
    next.delete("from");
    next.delete("to");
    setParams(next, { replace: true });
  };

  const totalDays = Math.max(1, dayIndex(from, to) + 1);
  const dayList = useMemo(
    () => Array.from({ length: totalDays }, (_, i) => addDays(from, i)),
    [from, totalDays],
  );

  // Destination colour map (stable order from the data).
  const destColor = useMemo(() => {
    const ids = new Set<string>();
    (sd?.assignments ?? []).forEach((a) => a.destinationId && ids.add(a.destinationId));
    (sd?.unassigned ?? []).forEach((u) => u.destinationId && ids.add(u.destinationId));
    const map = new Map<string, string>();
    [...ids].forEach((id, i) => map.set(id, DEST_PALETTE[i % DEST_PALETTE.length]));
    return map;
  }, [sd]);
  const colorFor = (destId?: string | null) => (destId && destColor.get(destId)) || "bg-gray-500";

  // Destination filter options — real destination names (from guide affiliations + assignments +
  // unassigned), sorted by `compareCzech`. Guides are planned destination-first, so this is the
  // primary planning axis.
  const destOptions = useMemo(() => {
    const m = new Map<string, string>();
    Object.values(sd?.guideDestinations ?? {}).forEach((list) =>
      list.forEach((d) => m.set(d.id, d.name)),
    );
    (sd?.assignments ?? []).forEach(
      (a) =>
        a.destinationId &&
        m.set(a.destinationId, a.destinationName ?? m.get(a.destinationId) ?? "—"),
    );
    (sd?.unassigned ?? []).forEach(
      (u) =>
        u.destinationId &&
        m.set(u.destinationId, u.destinationName ?? m.get(u.destinationId) ?? "—"),
    );
    return [...m.entries()].sort((a, b) => compareCzech(a[1], b[1]));
  }, [sd]);

  // Guide filter options A–Z (`compareCzech`); the timeline rows keep the API order
  // (guide `sortOrder`, then name).
  const guideOptions = useMemo(() => sortCzech(sd?.guides ?? [], (g) => g.name), [sd]);

  // Per-guide assignment buckets. We do NOT hide other-destination blocks under a destination filter —
  // the planner needs to see a guide's full commitments to judge if they're free; non-matching blocks
  // are dimmed in the row instead (see dimDest below).
  const assignmentsByGuide = useMemo(() => {
    const m = new Map<string, SAssignment[]>();
    (sd?.assignments ?? []).forEach((a) => {
      const arr = m.get(a.guideId) ?? [];
      arr.push(a);
      m.set(a.guideId, arr);
    });
    return m;
  }, [sd]);
  const availabilityByGuide = useMemo(() => {
    const m = new Map<string, SAvailability[]>();
    (sd?.availability ?? []).forEach((a) => {
      const arr = m.get(a.guideId) ?? [];
      arr.push(a);
      m.set(a.guideId, arr);
    });
    return m;
  }, [sd]);
  const cba = sd?.conflictsByAssignment ?? {};

  const visibleGuides = useMemo(() => {
    let gs = sd?.guides ?? [];
    if (fGuide) gs = gs.filter((g) => g.id === fGuide);
    if (onlyConflicts)
      gs = gs.filter((g) => (assignmentsByGuide.get(g.id) ?? []).some((a) => cba[a.id]));
    return gs;
  }, [sd, fGuide, onlyConflicts, assignmentsByGuide, cba]);

  // Destination-first grouping: under a destination filter the guides who work it lead the board;
  // everyone else becomes a clearly separated fallback pool (cross-destination stand-ins — used only
  // when the destination's own guides can't cover it).
  const worksDest = (gid: string) =>
    (sd?.guideDestinations?.[gid] ?? []).some((d) => d.id === fDest);
  // Within the primary pool, the destination's OWN team (home destination matches) leads, then those
  // who merely also work it.
  const primaryGuides = fDest
    ? visibleGuides
        .filter((g) => worksDest(g.id))
        .sort(
          (a, b) => Number(b.homeDestinationId === fDest) - Number(a.homeDestinationId === fDest),
        )
    : visibleGuides;
  const fallbackGuides = fDest ? visibleGuides.filter((g) => !worksDest(g.id)) : [];

  const filteredUnassigned = (sd?.unassigned ?? []).filter(
    (u) => !fDest || u.destinationId === fDest,
  );

  const handleDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    if (id.startsWith("asg:")) {
      const a = sd?.assignments.find((x) => `asg:${x.id}` === id);
      setDragLabel(a?.code || a?.tripTitle || t("planning.schedule.assignmentFallback"));
    } else if (id.startsWith("unasg:")) {
      const u = sd?.unassigned.find((x) => `unasg:${x.tripDateId}` === id);
      setDragLabel(u?.code || u?.tripTitle || t("planning.schedule.departureFallback"));
    }
  };
  const handleDragEnd = (e: DragEndEvent) => {
    setDragLabel(null);
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;
    // Drop one assignment block onto another → swap the two guides (single tracked step).
    if (overId.startsWith("asg-drop:") && activeId.startsWith("asg:")) {
      const sourceId = activeId.slice("asg:".length);
      const targetId = overId.slice("asg-drop:".length);
      if (sourceId !== targetId) swap.mutate({ assignmentAId: sourceId, assignmentBId: targetId });
      return;
    }
    if (!overId.startsWith("guide:")) return;
    const targetGuideId = overId.slice("guide:".length);
    if (activeId.startsWith("asg:")) {
      const a = sd?.assignments.find((x) => `asg:${x.id}` === activeId);
      if (a && a.guideId !== targetGuideId) reassign.mutate({ id: a.id, guideId: targetGuideId });
    } else if (activeId.startsWith("unasg:")) {
      const u = sd?.unassigned.find((x) => `unasg:${x.tripDateId}` === activeId);
      if (u) createAssignment.mutate({ guideId: targetGuideId, tripDateId: u.tripDateId });
    }
  };

  const conflictCount = sd?.conflicts.length ?? 0;
  const busy =
    reassign.isPending ||
    createAssignment.isPending ||
    updateAssignment.isPending ||
    removeAssignment.isPending ||
    swap.isPending;

  return (
    <div className="space-y-4">
      {/* Header + filters */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <CalendarRange className="h-6 w-6" /> {t("nav.items.schedule")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("planning.schedule.subtitle")}
            {conflictCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />{" "}
                {t("planning.schedule.conflicts", { count: conflictCount })}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Planning horizon — quarter (3 months) for season planning, 6 weeks for near-term detail */}
          <div className="flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
            {(["quarter", "6w"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSpan(s)}
                className={`px-2.5 py-1 text-xs font-medium ${
                  span === s
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {s === "quarter" ? t("planning.schedule.quarter") : t("planning.schedule.sixWeeks")}
              </button>
            ))}
          </div>
          {/* Window nav: prev · the visible range · next · jump-to-now */}
          <div className="flex items-center gap-1">
            <button
              className="btn-ghost btn-sm"
              onClick={() => shiftWindow(-1)}
              title={t("common.pagination.previous")}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium text-gray-700 dark:text-gray-200">
              {monthYear(from)}
              {monthYear(from) !== monthYear(to) ? ` – ${monthYear(to)}` : ""}
            </span>
            <button
              className="btn-ghost btn-sm"
              onClick={() => shiftWindow(1)}
              title={t("common.pagination.next")}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              className="btn-ghost btn-sm"
              title={t("planning.schedule.jumpToNow")}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("from");
                next.delete("to");
                setParams(next, { replace: true });
              }}
            >
              {t("planning.schedule.today")}
            </button>
          </div>
          <select
            className="input h-9 w-auto py-1 text-sm"
            value={fDest}
            onChange={(e) => setFilter("dest", e.target.value)}
          >
            <option value="">{t("planning.filters.allDestinations")}</option>
            {destOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="input h-9 w-auto py-1 text-sm"
            value={fGuide}
            onChange={(e) => setFilter("guide", e.target.value)}
          >
            <option value="">{t("planning.filters.allGuides")}</option>
            {guideOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={onlyConflicts}
              onChange={(e) => setFilter("conflicts", e.target.checked ? "1" : "")}
            />
            {t("planning.schedule.onlyConflicts")}
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center text-gray-500">
          {t("common.states.loading")}
        </div>
      ) : !sd || sd.guides.length === 0 ? (
        <div className="card flex h-48 flex-col items-center justify-center text-gray-500">
          <CalendarRange className="mb-2 h-10 w-10 text-gray-300" />
          <p>{t("planning.schedule.emptyGuides")}</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={swapAwareCollision}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="card overflow-x-auto p-0">
            <div style={{ width: LABEL_W + totalDays * dayW, minWidth: "100%" }}>
              {/* Day header */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <div
                  className="shrink-0 border-r border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 dark:border-gray-700 dark:bg-gray-800/50"
                  style={{ width: LABEL_W }}
                >
                  {t("planning.schedule.guideColumn")}
                </div>
                <div className="flex">
                  {dayList.map((d) => {
                    const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
                    const weekend = dow === 0 || dow === 6;
                    const first = d.endsWith("-01");
                    // Quarter is dense (13px/day) → label month starts + Mondays only; 6w labels every day.
                    const label = first
                      ? new Date(`${d}T00:00:00Z`).toLocaleDateString(uiLocale(), {
                          month: "short",
                        })
                      : span === "6w" || dow === 1
                        ? d.slice(8)
                        : "";
                    return (
                      <div
                        key={d}
                        className={`shrink-0 border-r py-1 text-center text-[10px] leading-tight ${
                          weekend ? "bg-gray-100 text-gray-400 dark:bg-gray-800" : "text-gray-500"
                        } ${first ? "border-gray-300 dark:border-gray-600" : "border-gray-100 dark:border-gray-800"}`}
                        style={{ width: dayW }}
                        title={d}
                      >
                        <div
                          className={`whitespace-nowrap ${first ? "font-semibold text-gray-700 dark:text-gray-200" : ""}`}
                        >
                          {label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Guide rows — destination's own pool first, then a fallback divider + the rest */}
              {primaryGuides.map((g) => (
                <GuideRow
                  key={g.id}
                  guide={g}
                  assignments={assignmentsByGuide.get(g.id) ?? []}
                  availability={availabilityByGuide.get(g.id) ?? []}
                  from={from}
                  totalDays={totalDays}
                  cba={cba}
                  colorFor={colorFor}
                  dayW={dayW}
                  dimDest={fDest || null}
                  onSelect={(a, x, y) => setSelected({ a, x, y })}
                />
              ))}
              {fDest && fallbackGuides.length > 0 && (
                <div
                  className="flex items-center gap-1.5 border-y border-dashed border-gray-300 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-500 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-400"
                  style={{ width: LABEL_W + totalDays * dayW }}
                >
                  <UserPlus className="h-3.5 w-3.5" />{" "}
                  {t("planning.schedule.fallbackPool", { count: fallbackGuides.length })}
                </div>
              )}
              {fallbackGuides.map((g) => (
                <GuideRow
                  key={g.id}
                  guide={g}
                  assignments={assignmentsByGuide.get(g.id) ?? []}
                  availability={availabilityByGuide.get(g.id) ?? []}
                  from={from}
                  totalDays={totalDays}
                  cba={cba}
                  colorFor={colorFor}
                  dayW={dayW}
                  dimDest={fDest || null}
                  onSelect={(a, x, y) => setSelected({ a, x, y })}
                />
              ))}
            </div>
          </div>

          {/* Unassigned tray */}
          <div className="card mt-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <Flag className="h-4 w-4 text-amber-500" /> {t("planning.schedule.unassignedTitle")}
              <span className="font-normal text-gray-400">({filteredUnassigned.length})</span>
              <span className="ml-2 text-xs font-normal text-gray-400">
                {t("planning.schedule.unassignedHint")}
              </span>
            </div>
            {filteredUnassigned.length === 0 ? (
              <p className="text-sm text-gray-400">{t("planning.schedule.allAssigned")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {filteredUnassigned.map((u) => (
                  <UnassignedChip key={u.tripDateId} u={u} colorFor={colorFor} />
                ))}
              </div>
            )}
          </div>

          <DragOverlay>
            {dragLabel ? (
              <div className="rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg">
                {dragLabel}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Block action popover */}
      {selected && (
        <BlockPopover
          a={selected.a}
          x={selected.x}
          y={selected.y}
          busy={busy}
          onClose={() => setSelected(null)}
          onStatus={(status) => {
            updateAssignment.mutate({ id: selected.a.id, patch: { status } });
            setSelected(null);
          }}
          onRole={(role) => {
            updateAssignment.mutate({ id: selected.a.id, patch: { role } });
            setSelected(null);
          }}
          onReplace={(guideId) => {
            // Reassign THIS departure to the chosen guide as tentative (a replacement is unconfirmed
            // until the planner confirms it) — the block hops to the new guide's row.
            updateAssignment.mutate({
              id: selected.a.id,
              patch: { guideId, status: "tentative" },
            });
            setSelected(null);
          }}
          onRemove={() => {
            removeAssignment.mutate(selected.a.id);
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

// ── Guide row (droppable track) ──
function GuideRow({
  guide,
  assignments,
  availability,
  from,
  totalDays,
  cba,
  colorFor,
  dayW,
  dimDest,
  onSelect,
}: {
  guide: SGuide;
  assignments: SAssignment[];
  availability: SAvailability[];
  from: string;
  totalDays: number;
  cba: Record<string, { severity: ScheduleConflictSeverity; conflicts: SConflict[] }>;
  colorFor: (destId?: string | null) => string;
  dayW: number;
  dimDest: string | null; // when set, blocks of OTHER destinations are dimmed (kept visible)
  onSelect: (a: SAssignment, x: number, y: number) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: `guide:${guide.id}` });
  return (
    <div className="flex border-b border-gray-100 dark:border-gray-800">
      <div
        className="shrink-0 border-r border-gray-200 px-3 py-2 dark:border-gray-700"
        style={{ width: LABEL_W }}
      >
        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {guide.name}
        </div>
        {guide.homeDestinationName ? (
          <div
            className="truncate text-xs text-violet-500"
            title={t("planning.schedule.homeDestination", { name: guide.homeDestinationName })}
          >
            ⌂ {guide.homeDestinationName}
          </div>
        ) : guide.livingIn ? (
          <div className="truncate text-xs text-gray-400">{guide.livingIn}</div>
        ) : null}
      </div>
      <div
        ref={setNodeRef}
        className={`relative ${isOver ? "bg-violet-50 dark:bg-violet-900/20" : ""}`}
        style={{ width: totalDays * dayW, height: ROW_H }}
      >
        {/* weekend gridlines */}
        {Array.from({ length: totalDays }, (_, i) => {
          const dow = new Date(Date.parse(`${from}T00:00:00Z`) + i * DAY_MS).getUTCDay();
          if (dow !== 0 && dow !== 6) return null;
          return (
            <div
              key={i}
              className="absolute bottom-0 top-0 bg-gray-50 dark:bg-gray-800/40"
              style={{ left: i * dayW, width: dayW }}
            />
          );
        })}
        {/* availability bands */}
        {availability.map((av) => {
          const s = clamp(dayIndex(from, av.fromDate), 0, totalDays - 1);
          const e = clamp(dayIndex(from, av.toDate), 0, totalDays - 1);
          if (dayIndex(from, av.toDate) < 0 || dayIndex(from, av.fromDate) > totalDays - 1)
            return null;
          return (
            <div
              key={av.id}
              className="absolute bottom-0 top-0 bg-[repeating-linear-gradient(45deg,rgba(120,120,120,0.15)_0,rgba(120,120,120,0.15)_6px,transparent_6px,transparent_12px)]"
              style={{ left: s * dayW, width: (e - s + 1) * dayW }}
              title={`${enumLabel(GUIDE_AVAILABILITY_REASON_LABELS, av.reason)} ${av.fromDate}–${av.toDate}`}
            />
          );
        })}
        {/* assignment blocks */}
        {assignments.map((a) => (
          <AssignmentBlock
            key={a.id}
            a={a}
            from={from}
            totalDays={totalDays}
            severity={cba[a.id]?.severity}
            tooltip={cba[a.id]?.conflicts.map((x) => x.message).join(" · ")}
            color={colorFor(a.destinationId)}
            dayW={dayW}
            dim={!!dimDest && a.destinationId !== dimDest}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ── Assignment block (draggable) ──
function AssignmentBlock({
  a,
  from,
  totalDays,
  severity,
  tooltip,
  color,
  dayW,
  dim,
  onSelect,
}: {
  a: SAssignment;
  from: string;
  totalDays: number;
  severity?: ScheduleConflictSeverity;
  tooltip?: string;
  color: string;
  dayW: number;
  dim?: boolean; // other-destination block under a destination filter — visible but de-emphasised
  onSelect: (a: SAssignment, x: number, y: number) => void;
}) {
  const { t } = useTranslation();
  // The block is BOTH draggable (drag it to reassign) AND a drop target (drop another block on it to
  // swap) — combine the two node refs onto the one element.
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `asg:${a.id}`,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `asg-drop:${a.id}` });
  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };
  const startIdx = clamp(dayIndex(from, a.startDate), 0, totalDays - 1);
  const endIdx = clamp(dayIndex(from, a.endDate), 0, totalDays - 1);
  const left = startIdx * dayW;
  const width = (endIdx - startIdx + 1) * dayW;
  const tentative = a.status === "tentative";
  const ring = isOver
    ? "ring-2 ring-violet-500" // swap target while another block hovers
    : severity === "red"
      ? "ring-2 ring-red-500"
      : severity === "orange"
        ? "ring-2 ring-amber-500"
        : "";
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={(e) => onSelect(a, e.clientX, e.clientY)}
      className={`absolute top-1.5 flex cursor-grab items-center gap-1 overflow-hidden rounded px-1.5 text-[11px] font-medium text-white ${color} ${ring} ${
        tentative
          ? "border-2 border-dashed border-white/70 opacity-70"
          : "border-2 border-transparent"
      } ${isDragging ? "opacity-30" : dim ? "opacity-40 grayscale" : ""}`}
      style={{ left: left + 1, width: Math.max(dayW - 2, width - 2), height: ROW_H - 14 }}
      title={`${a.tripTitle ?? a.code ?? t("planning.schedule.departureFallback")} · ${formatDate(a.startDate)}–${formatDate(a.endDate)} · ${enumLabel(GUIDE_ASSIGNMENT_ROLE_LABELS, a.role)}${tentative ? ` ${t("planning.schedule.tentativeSuffix")}` : ""}${tooltip ? ` · ⚠ ${tooltip}` : ""}`}
    >
      <span className="rounded bg-black/25 px-1 text-[9px] leading-none">
        {ROLE_INITIAL[a.role]}
      </span>
      <span className="truncate">{a.code || a.tripTitle || "—"}</span>
      {severity && <AlertTriangle className="h-3 w-3 shrink-0" />}
    </div>
  );
}

// ── Unassigned chip (draggable) ──
function UnassignedChip({
  u,
  colorFor,
}: {
  u: SUnassigned;
  colorFor: (destId?: string | null) => string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `unasg:${u.tripDateId}`,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex cursor-grab items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-800 ${
        isDragging ? "opacity-30" : ""
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${colorFor(u.destinationId)}`} />
      {u.code && <span className="font-mono text-gray-500">{u.code}</span>}
      <span className="truncate text-gray-700 dark:text-gray-200">{u.tripTitle ?? "—"}</span>
      <span className="text-gray-400">{formatDate(u.startDate)}</span>
    </div>
  );
}

// ── Block action popover ──
function BlockPopover({
  a,
  x,
  y,
  busy,
  onClose,
  onStatus,
  onRole,
  onReplace,
  onRemove,
}: {
  a: SAssignment;
  x: number;
  y: number;
  busy: boolean;
  onClose: () => void;
  onStatus: (s: GuideAssignmentStatus) => void;
  onRole: (r: GuideAssignmentRole) => void;
  onReplace: (guideId: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [showRepl, setShowRepl] = useState(false);
  const { data: replData, isLoading: replLoading } = useQuery({
    queryKey: ["replacements", a.id],
    queryFn: () =>
      api.get<{ data: { candidates: ReplacementCandidate[] } }>(
        `/api/admin/schedule/assignments/${a.id}/replacements`,
      ),
    enabled: showRepl,
  });
  const candidates = replData?.data.candidates ?? [];
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="fixed z-50 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-800"
      style={{
        left: Math.min(x, window.innerWidth - 272),
        top: Math.min(y, window.innerHeight - 260),
      }}
    >
      <div className="mb-2 flex items-start justify-between">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">
          {a.code || a.tripTitle || t("planning.schedule.assignmentFallback")}
        </div>
        <button
          title={t("common.actions.close")}
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-2 text-xs text-gray-500">
        {formatDate(a.startDate)} – {formatDate(a.endDate)}
      </div>

      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
        {t("planning.schedule.role")}
      </label>
      <select
        className="input mb-3 h-8 py-0 text-sm"
        value={a.role}
        disabled={busy}
        onChange={(e) => onRole(e.target.value as GuideAssignmentRole)}
      >
        {GUIDE_ASSIGNMENT_ROLES.map((r) => (
          <option key={r} value={r}>
            {enumLabel(GUIDE_ASSIGNMENT_ROLE_LABELS, r)}
          </option>
        ))}
      </select>

      {a.status === "tentative" ? (
        <button
          className="btn-primary btn-sm mb-2 w-full"
          disabled={busy}
          onClick={() => onStatus("confirmed")}
        >
          {t("common.actions.confirm")}
        </button>
      ) : (
        <button
          className="btn-secondary btn-sm mb-2 w-full"
          disabled={busy}
          onClick={() => onStatus("tentative")}
        >
          {t("planning.schedule.makeTentative")}
        </button>
      )}

      {/* Find replacement (R7) — feasible guides first; click one to reassign as tentative. */}
      {!showRepl ? (
        <button
          className="btn-ghost btn-sm mb-2 w-full"
          disabled={busy}
          onClick={() => setShowRepl(true)}
        >
          <UserPlus className="h-4 w-4" /> {t("planning.schedule.findReplacement")}
        </button>
      ) : (
        <div className="mb-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {t("planning.schedule.whoCanCover")}
            </span>
            {replLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          </div>
          {!replLoading && candidates.length === 0 ? (
            <p className="text-xs text-gray-400">{t("planning.schedule.noOtherGuides")}</p>
          ) : (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {candidates.slice(0, 6).map((cnd) => (
                <li key={cnd.guideId}>
                  <button
                    disabled={!cnd.feasible || busy}
                    onClick={() => cnd.feasible && onReplace(cnd.guideId)}
                    className={`flex w-full items-start gap-1.5 rounded px-1.5 py-1 text-left text-xs ${
                      cnd.feasible
                        ? "hover:bg-violet-50 dark:hover:bg-violet-900/20"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    title={cnd.warnings.join(" · ") || undefined}
                  >
                    {cnd.feasible ? (
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                    )}
                    <span className="min-w-0">
                      <span className="font-medium text-gray-800 dark:text-gray-100">
                        {cnd.guideName}
                      </span>
                      {cnd.warnings.length > 0 && (
                        <span
                          className={`block truncate ${cnd.feasible ? "text-amber-600" : "text-red-500"}`}
                        >
                          {cnd.warnings[0]}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        className="btn-ghost btn-sm w-full text-red-600 hover:bg-red-50"
        disabled={busy}
        onClick={onRemove}
      >
        {t("planning.schedule.removeFromDeparture")}
      </button>

      {a.tripId && (
        <Link
          to={`/departures/${a.tripDateId}`}
          className="mt-2 block text-center text-xs text-violet-600 hover:underline"
        >
          {t("planning.schedule.openDepartureVelin")}
        </Link>
      )}
    </div>
  );
}
